import {
  DataPointValue,
  DateRangeFilter,
  ICustomReportFilter,
  ICustomReportFilterValue,
  ReportFilterComparison,
  TimingDirection,
} from '@principle-theorem/principle-core/interfaces';
import {
  BigQueryComparison,
  BigQuerySpecialComparison,
  IAccessorContext,
  REPORT_FILTER_BIG_QUERY_COMPARISON_MAP,
} from '@principle-theorem/reporting/interfaces';
import { isArray, serialise } from '@principle-theorem/shared';
import { compact, first, isString } from 'lodash';
import * as moment from 'moment-timezone';
import { BigQuerySQL, IUnnestPath } from './big-query-sql';
import { CustomReportFilter } from './models/custom-report-filters';

function logInvalidFilterValue(
  comparison: ReportFilterComparison,
  filterValue: unknown
): void {
  const logValue = JSON.stringify(serialise(filterValue));
  // eslint-disable-next-line no-console
  console.warn(
    `Invalid filter value for comparison "${comparison}". ${logValue}`
  );
}

export function getCustomReportFilterValueSQLs(
  dataPropertyPath: string,
  filter: ICustomReportFilter,
  comparison: ReportFilterComparison,
  context: IAccessorContext,
  convertValuesToNull: (DataPointValue | DateRangeFilter)[] = []
): string[] {
  const sqlFilters = filter.values.map((filterValue) => {
    if (isNullComparison(filterValue, convertValuesToNull)) {
      return BigQuerySQL.filter(dataPropertyPath, 'IS', undefined);
    }
    if (isDateRangeComparison(comparison)) {
      return dateRangeFilterSQL(dataPropertyPath, filterValue, context);
    }
    if (isDateComparison(comparison)) {
      return dateFilterSQL(dataPropertyPath, filter, filterValue, context);
    }
    if (isArray(filterValue.value)) {
      logInvalidFilterValue(comparison, filterValue.value);
      return;
    }
    return BigQuerySQL.filter(
      dataPropertyPath,
      getBigQueryComparison(filter, comparison),
      filterValue.value
    );
  });

  return compact(sqlFilters);
}

export function getCustomReportFilterSQL(
  dataPropertyPath: string,
  filter: ICustomReportFilter,
  context: IAccessorContext,
  convertValuesToNull: (DataPointValue | DateRangeFilter)[] = [],
  treatNotEqualAsEqual: boolean = false
): string {
  const initialComparison = CustomReportFilter.getComparison(filter);
  const comparison = treatNotEqualAsEqual
    ? convertToIsEqual(initialComparison)
    : initialComparison;

  const filters = getCustomReportFilterValueSQLs(
    dataPropertyPath,
    filter,
    comparison,
    context,
    convertValuesToNull
  );
  if (filters.length === 0) {
    return '';
  }

  const isNotFilter = [
    ReportFilterComparison.IsNotEqualTo,
    ReportFilterComparison.TextDoesNotContain,
  ];
  return isNotFilter.includes(comparison)
    ? BigQuerySQL.and(filters)
    : BigQuerySQL.or(filters);
}

function convertToIsEqual(
  comparison: ReportFilterComparison
): ReportFilterComparison {
  if (comparison === ReportFilterComparison.IsNotEqualTo) {
    return ReportFilterComparison.IsEqualTo;
  }
  if (comparison === ReportFilterComparison.TextDoesNotContain) {
    return ReportFilterComparison.TextContains;
  }
  return comparison;
}

export function getBigQueryComparison(
  filter: ICustomReportFilter,
  comparison: ReportFilterComparison
): BigQueryComparison | BigQuerySpecialComparison {
  const direction = filter.relativeOffset?.direction ?? TimingDirection.After;
  if (comparison === ReportFilterComparison.DateIsAtMost) {
    return direction === TimingDirection.After ? '<=' : '>=';
  }
  if (comparison === ReportFilterComparison.DateIsAtLeast) {
    return direction === TimingDirection.After ? '>=' : '<=';
  }
  return REPORT_FILTER_BIG_QUERY_COMPARISON_MAP[comparison];
}

function isDateRangeComparison(comparison: ReportFilterComparison): boolean {
  return comparison === ReportFilterComparison.DateIsBetween;
}

function isDateComparison(comparison: ReportFilterComparison): boolean {
  const dateComparisons = [
    ReportFilterComparison.DateIs,
    ReportFilterComparison.DateIsBefore,
    ReportFilterComparison.DateIsAfter,
    ReportFilterComparison.DateIsBetween,
    ReportFilterComparison.DateIsAtLeast,
    ReportFilterComparison.DateIsAtMost,
  ];
  return dateComparisons.includes(comparison);
}

function isNullComparison(
  filterValue: ICustomReportFilterValue,
  convertValuesToNull: (DataPointValue | DateRangeFilter)[] = []
): boolean {
  return convertValuesToNull.includes(filterValue.value);
}

function dateRangeFilterSQL(
  dataPropertyPath: string,
  filterValue: ICustomReportFilterValue,
  context: IAccessorContext
): string | undefined {
  if (!isArray(filterValue.value)) {
    logInvalidFilterValue(
      ReportFilterComparison.DateIsBetween,
      filterValue.value
    );
    return;
  }
  const [from, to] = filterValue.value;
  const fromValue = moment(from)
    .tz(context.timezone)
    .startOf('day')
    .toISOString();
  const toValue = moment(to).tz(context.timezone).endOf('day').toISOString();
  return BigQuerySQL.dateRange(dataPropertyPath, fromValue, toValue);
}

function dateFilterSQL(
  dataPropertyPath: string,
  filter: ICustomReportFilter,
  filterValue: ICustomReportFilterValue,
  context: IAccessorContext
): string | undefined {
  const comparison = CustomReportFilter.getComparison(filter);
  const value = isArray(filterValue.value)
    ? first(filterValue.value)
    : filterValue.value;

  if (!isString(value)) {
    logInvalidFilterValue(comparison, filterValue.value);
    return;
  }

  const momentValue = moment(value).tz(context.timezone);
  const startOfDay = moment(momentValue).startOf('day').toISOString();
  const endOfDay = moment(momentValue).endOf('day').toISOString();

  const direction = filter.relativeOffset?.direction ?? TimingDirection.After;

  switch (comparison) {
    case ReportFilterComparison.DateIs:
      return BigQuerySQL.dateRange(dataPropertyPath, startOfDay, endOfDay);

    case ReportFilterComparison.DateIsAfter:
      return BigQuerySQL.filter(
        BigQuerySQL.castTimestamp(dataPropertyPath, true),
        '>=',
        BigQuerySQL.castTimestamp(startOfDay),
        false
      );

    case ReportFilterComparison.DateIsBefore:
      return BigQuerySQL.filter(
        BigQuerySQL.castTimestamp(dataPropertyPath, true),
        '<=',
        BigQuerySQL.castTimestamp(endOfDay),
        false
      );

    case ReportFilterComparison.DateIsAtLeast:
      if (direction === TimingDirection.After) {
        return BigQuerySQL.filter(
          BigQuerySQL.castTimestamp(dataPropertyPath, true),
          '>=',
          BigQuerySQL.castTimestamp(startOfDay),
          false
        );
      }
      return BigQuerySQL.filter(
        BigQuerySQL.castTimestamp(dataPropertyPath, true),
        '<=',
        BigQuerySQL.castTimestamp(endOfDay),
        false
      );

    case ReportFilterComparison.DateIsAtMost:
      if (direction === TimingDirection.After) {
        return BigQuerySQL.filter(
          BigQuerySQL.castTimestamp(dataPropertyPath, true),
          '<=',
          BigQuerySQL.castTimestamp(endOfDay),
          false
        );
      }
      return BigQuerySQL.filter(
        BigQuerySQL.castTimestamp(dataPropertyPath, true),
        '>=',
        BigQuerySQL.castTimestamp(startOfDay),
        false
      );

    default:
      return BigQuerySQL.filter(
        BigQuerySQL.castTimestamp(dataPropertyPath, true),
        getBigQueryComparison(filter, comparison),
        BigQuerySQL.castTimestamp(startOfDay, false),
        false
      );
  }
}

export function objectInArrayFilterSQL(
  unnestPaths: IUnnestPath[],
  refPath: string,
  filter: ICustomReportFilter,
  context: IAccessorContext
): string {
  const comparison = CustomReportFilter.getComparison(filter);
  const notInArray = comparison === ReportFilterComparison.IsNotEqualTo;
  const whereCondition = getCustomReportFilterSQL(
    refPath,
    filter,
    context,
    [],
    notInArray
  );
  return BigQuerySQL.objectInArray(unnestPaths, whereCondition, notInArray);
}
